import React, { useState } from 'react';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import Settings from 'lucide-react/dist/esm/icons/settings.js';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up.js';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import Camera from 'lucide-react/dist/esm/icons/camera.js';
import Image from 'lucide-react/dist/esm/icons/image.js';
import RotateCcw from 'lucide-react/dist/esm/icons/rotate-ccw.js';
import Plus from 'lucide-react/dist/esm/icons/plus.js';
import X from 'lucide-react/dist/esm/icons/x.js';
import { Card, Stack } from '../layout';
import { useTranslations } from '../../contexts/TranslationContext';
import { useContent } from '../../contexts/ContentContext';
import InlineTranslationEdit from '../InlineTranslationEdit';
import AdditionalDrawDetailModal from '../AdditionalDrawDetailModal';
import ImagePickerModal from '../ImagePickerModal';
import { useSectionControl } from '../../contexts/SectionControlContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { parseDrawDate, getDrawIanaTimeZone } from '../../utils/dateFormat';
import { resolveDrawImage } from './AdditionalPrizesShowcase';

export default function BonusDrawsList({ bonusDraws = [], editMode = false, handleTranslationUpdate = () => {} }) {
  const { t } = useTranslations();
  const { c } = useContent();
  const [selectedDraw, setSelectedDraw] = useState(null);
  
  const sectionControl = useSectionControl();
  const section = sectionControl?.getSectionByType?.('bonus_draws');
  const config = section?.config || {};
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null);

  const { isAdmin: resolvedIsAdmin } = useIsAdmin();
  const showSettings = editMode || resolvedIsAdmin;

  const onUpdateConfig = (patch) => {
    if (section && sectionControl?.updateConfig) {
      sectionControl.updateConfig(section.id, { ...config, ...patch });
    }
  };

  const handleDrawClick = (draw) => {
    if (!editMode) {
      setSelectedDraw(draw);
    }
  };

  const handleCloseModal = () => {
    setSelectedDraw(null);
  };

  const enrichedBonusDraws = (bonusDraws || []).map(d => ({
    ...d,
    image_url: resolveDrawImage(d, config)
  }));
  
  if (!bonusDraws || bonusDraws.length === 0) {
    return (
      <Card 
        background="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20"
        className="border-2 border-amber-200 dark:border-amber-800"
      >
        <div className="flex items-center gap-2 mb-2">
          <Gift className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          <h3 className="text-lg font-bold text-amber-900 dark:text-amber-100">
            <InlineTranslationEdit
              translationKey="BonusDrawsTitle"
              value={c('BonusDrawsTitle', t('bonusDraws.title'))}
              fallback="Bonus Entry Draws"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </h3>
        </div>
        <p className="text-sm text-amber-800 dark:text-amber-200 text-center py-4">
          <InlineTranslationEdit
            translationKey="BonusDrawsEmpty"
            value={c('BonusDrawsEmpty', t('bonusDraws.empty'))}
            fallback="No bonus draws available at this time."
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>
      </Card>
    );
  }

  return (
    <>
      <Card 
        background="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20"
        className="border-2 border-amber-200 dark:border-amber-800 relative pt-10"
      >
        {/* Inline Admin Settings Panel */}
        {showSettings && (
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-40 flex justify-center">
            <div className="relative">
              <button
                type="button"
                onClick={() => setSettingsOpen(!settingsOpen)}
                className="rounded-b-lg bg-black/85 backdrop-blur-md border border-t-0 border-white/20 px-3 py-1.5 text-[10px] font-bold text-white/70 hover:text-white hover:bg-black transition-colors flex items-center gap-1.5 cursor-pointer shadow-[0_4px_12px_rgba(0,0,0,0.5)] border-x"
              >
                <Settings className="w-3.5 h-3.5 animate-[spin_10s_linear_infinite] text-amber-400" />
                {settingsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>

              {settingsOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-80 p-4 rounded-2xl bg-black/90 border border-white/20 text-white shadow-2xl backdrop-blur-xl z-50 text-xs space-y-3 animate-fade-in normal-case">
                  <div className="font-bold text-sm text-amber-400 border-b border-white/10 pb-2 flex items-center gap-1.5">
                    <Settings className="w-4 h-4 text-amber-400 animate-pulse" /> {t('admin.bonusDrawsSettings.title', 'Bonus Draws Settings')}
                  </div>

                  {/* Fallback Image */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 font-medium">{t('admin.bonusDrawsSettings.defaultFallbackImage', 'Default Fallback Image')}</span>
                      {config.defaultPrizeImage && (
                        <button
                          type="button"
                          onClick={() => onUpdateConfig({ defaultPrizeImage: '' })}
                          className="text-red-400 hover:text-red-300 font-medium text-[10px]"
                        >
                          {t('admin.sections.reset', 'Reset')}
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setPickerTarget({ type: 'default' })}
                      className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded bg-white/10 text-white font-medium hover:bg-white/20 transition-all border border-white/10 cursor-pointer"
                    >
                      {config.defaultPrizeImage ? (
                        <>
                          <RotateCcw className="w-3.5 h-3.5 text-amber-400" /> {t('admin.bonusDrawsSettings.changeFallbackImage', 'Change Fallback Image')}
                        </>
                      ) : (
                        <>
                          <Plus className="w-3.5 h-3.5 text-amber-400" /> {t('admin.bonusDrawsSettings.chooseFallbackImage', 'Choose Fallback Image')}
                        </>
                      )}
                    </button>
                    {config.defaultPrizeImage && (
                      <div className="rounded overflow-hidden border border-white/15 h-16 relative bg-black/40">
                        <img src={config.defaultPrizeImage} alt="Fallback preview" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>

                  {/* Per-Draw Custom Images */}
                  <div className="pt-2 border-t border-white/10 space-y-2">
                    <span className="text-gray-400 font-medium block">{t('admin.bonusDrawsSettings.customPrizeImages', 'Custom Prize Images')}</span>
                    <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                      {enrichedBonusDraws.map((d) => {
                        const customImages = config.customDrawImages || {};
                        const hasCustom = !!customImages[d.id];
                        const resolvedImg = resolveDrawImage(d, config);

                        return (
                          <div key={d.id} className="flex items-center justify-between gap-2 p-1.5 rounded bg-white/5 border border-white/10">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {resolvedImg ? (
                                <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0 bg-black/50 border border-white/10">
                                  <img src={resolvedImg} alt="" className="w-full h-full object-cover" />
                                </div>
                              ) : (
                                <div className="w-8 h-8 rounded bg-gray-800 flex items-center justify-center flex-shrink-0 border border-white/10">
                                  <Image className="w-4 h-4 text-white/40" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-bold truncate text-[11px] text-white leading-tight">{d.title}</p>
                                <p className="text-[9px] text-gray-400 leading-none mt-0.5">
                                  {hasCustom ? t('admin.bonusDrawsSettings.customImage', 'Custom Image') : t('admin.bonusDrawsSettings.originalImage', 'Original Image')}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => setPickerTarget({ type: 'draw', drawId: d.id })}
                                className="p-1 rounded bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
                                title={t('admin.bonusDrawsSettings.setCustomImage', 'Set Custom Image')}
                              >
                                <Camera className="w-3.5 h-3.5 text-amber-300" />
                              </button>
                              {hasCustom && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentCustom = { ...config.customDrawImages };
                                    delete currentCustom[d.id];
                                    onUpdateConfig({ customDrawImages: currentCustom });
                                  }}
                                  className="p-1 rounded bg-red-950/40 hover:bg-red-900/40 text-red-400 transition-colors cursor-pointer"
                                  title={t('admin.bonusDrawsSettings.removeCustomImage', 'Remove Custom Image')}
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mb-4">
          <Gift className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          <h3 className="text-lg font-bold text-amber-900 dark:text-amber-100">
            <InlineTranslationEdit
              translationKey="BonusDrawsTitle"
              value={c('BonusDrawsTitle', t('bonusDraws.title'))}
              fallback="Bonus Entry Draws"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </h3>
        </div>
        <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">
          <InlineTranslationEdit
            translationKey="BonusDrawsSubtitle"
            value={c('BonusDrawsSubtitle', t('bonusDraws.subtitle'))}
            fallback="Every ticket purchase gives you automatic entry into these bonus draws for extra chances to win!"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>
        <Stack spacing={3}>
          {enrichedBonusDraws.map((draw) => (
            <BonusDrawItem 
              key={draw.id} 
              draw={draw} 
              config={config}
              editMode={editMode} 
              handleTranslationUpdate={handleTranslationUpdate}
              onClick={() => handleDrawClick(draw)}
            />
          ))}
        </Stack>
      </Card>

      <AdditionalDrawDetailModal
        isOpen={!!selectedDraw}
        onClose={handleCloseModal}
        draw={selectedDraw}
      />

      {pickerTarget && (
        <ImagePickerModal
          onClose={() => setPickerTarget(null)}
          onSelect={(url) => {
            if (pickerTarget.type === 'default') {
              onUpdateConfig({ defaultPrizeImage: url });
            } else if (pickerTarget.type === 'draw') {
              const currentCustom = config.customDrawImages || {};
              onUpdateConfig({
                customDrawImages: { ...currentCustom, [pickerTarget.drawId]: url }
              });
            }
            setPickerTarget(null);
          }}
        />
      )}
    </>
  );
}

function BonusDrawItem({ draw, editMode, handleTranslationUpdate, onClick }) {
  const { t } = useTranslations();
  const { c } = useContent();

  const prizeAmount = Number(draw.prize_value ?? draw.current_jackpot ?? 0);
  const formattedPrize = prizeAmount > 0
    ? `$${prizeAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : null;

  const drawDateText = draw.draw_date
    ? formatDrawDate(draw)
    : null;

  const drawTimeText = draw.draw_date
    ? formatDrawTime(draw)
    : null;

  const drawLabel = [drawDateText, drawTimeText].filter(Boolean).join(' \u00b7 ');

  function formatDrawDate(source) {
    const d = parseDrawDate(source);
    if (!d) return null;
    const tz = getDrawIanaTimeZone(source);
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    if (tz) opts.timeZone = tz;
    return d.toLocaleDateString(undefined, opts);
  }

  function formatDrawTime(source) {
    const d = parseDrawDate(source);
    if (!d) return null;
    const tz = getDrawIanaTimeZone(source);
    const opts = { hour: 'numeric', minute: '2-digit' };
    if (tz) opts.timeZone = tz;
    return d.toLocaleTimeString(undefined, opts);
  }

  return (
    <div 
      className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden border border-amber-200 dark:border-amber-700 shadow-sm hover:shadow-md transition-all cursor-pointer"
      onClick={onClick}
    >
      <div className="flex flex-col sm:flex-row">
        {draw.image_url && (
          <div className="sm:w-40 sm:flex-shrink-0 h-32 sm:h-auto bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <img
              src={draw.image_url}
              alt={draw.title}
              loading="lazy"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        )}
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0">
              <h4 className="font-bold text-gray-900 dark:text-white text-base leading-tight">
                {draw.title}
              </h4>
              {draw.draw_number != null && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                  {t('bonusDraws.drawNumber', 'Draw Number')}: #{draw.draw_number}
                </p>
              )}
            </div>
            {formattedPrize && (
              <div className="text-lg font-bold text-green-600 dark:text-green-400 whitespace-nowrap">
                {formattedPrize}
              </div>
            )}
          </div>
          {draw.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
              {draw.description}
            </p>
          )}
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            {drawLabel && <span>{drawLabel}</span>}
            {draw.total_tickets_sold != null && (
              <span>
                {draw.total_tickets_sold.toLocaleString()} {c('BonusDrawsTickets', t('bonusDraws.tickets'))}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
